import React, { useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import flowRight from 'lodash-es/flowRight';
import { policyType as PolicyType } from '@ourbranch/lookups';

import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import withMoratorium from 'core/hooks/with-moratorium';
import { withStore } from 'core/store';
import { AuthContext } from 'core/components/auth';
import { isBranchEnv } from 'brand-config';
import { InternalUwNotes } from 'common/components/internal-uw-notes';
import { AccountDetails } from '../account-details';
import { AgentDetails } from '../agent-details';
import { agentDetailsSourceFromPolicy } from '../agent-details/agent-details-source';
import HoldCards from './hold-cards';
import { PropertyInspection } from './property-inspection';
import SegmentList from './segments/segment-list';
import { Provider, Versions } from './versions';
import useStyles from './policy-sidebar.styles';

const initialOrder = {
  order: 'desc',
  orderBy: (a, b) => (a.version < b.version ? 1 : -1)
};

const sort = (list, orderBy) => list.sort(orderBy);

const processOrder = (list, { order, orderBy }) => {
  if (order === 'desc') {
    return sort(list, orderBy);
  }
  return sort(list, orderBy).reverse();
};

const SegmentGroup = {
  renewal: 'renewal',
  history: 'history'
};

const getSegmentGroup = (segment, policy) =>
  new Date(segment.startDate) > new Date(policy && policy.endDate) ? SegmentGroup.renewal : SegmentGroup.history;

const groupSegments = (segments = [], policy) =>
  segments.reduce(
    (acc, segment) => {
      const group = getSegmentGroup(segment, policy);

      return { ...acc, [group]: [...acc[group], segment] };
    },
    { [SegmentGroup.history]: [], [SegmentGroup.renewal]: [] }
  );

export const renewalSectionTitle = (branchEnv) => (branchEnv ? 'Renewal Changes' : 'Renewal History');
export const sectionCount = (branchEnv, count) => (branchEnv ? `${count} Segments` : `${count}`);

const PolicySidebar = ({
  store: {
    account: {
      policies: {
        policy: { loading, policy, changed, reversedSegments, activeHoldCards, allActiveHoldCards }
      }
    }
  }
}) => {
  const classes = useStyles();
  const { canEdit } = useContext(AuthContext);
  const initialVersionHistoryList = policy && policy.versionHistory ? policy.versionHistory : [];

  const [order, setOrder] = useState(initialOrder);
  const [activeSection, setActiveSection] = useState(SegmentGroup.history);
  const list = processOrder(initialVersionHistoryList.slice(), initialOrder);
  const versions = list.slice(0, 5);
  const total = list.length;

  const showHoldCards = activeHoldCards().length > 0 && policy && policy.state;

  const segments = reversedSegments();
  const { history, renewal } = useMemo(() => groupSegments(segments, policy), [segments, policy]);

  return (
    <div className={changed ? classes.changed : null}>
      <Provider value={{ setOrder, order }}>
        {/* Staff-only internal UW notes — self-gates on the session's isAgency flag; never shown to agents. #35208 */}
        {policy?.id && <InternalUwNotes policyId={policy.id} />}
        {canEdit && policy?.policyType === PolicyType.Home && (
          <Section classes={{ container: classes.firstContainer }} title="Property Inspection">
            {loading || !policy ? <Loading /> : <PropertyInspection />}
          </Section>
        )}
        {/* Account contact details surfaced on the policy page so staff don't have to
            navigate back to the account landing page mid-workflow (ticket #26540). The account
            store is already populated by useCustomerProvider. disableAccountRouteEffects skips
            the account-route-only redirect guard + openAccountCard auto-edit, since the policy
            route owns navigation here; editing via the pencil still works. */}
        <AccountDetails disableAccountRouteEffects />
        {/* Agent / agency / lead source for THIS policy (any status), collapsed by default, so staff
            working an agent-involved policy don't have to go back to the Customer page (#35737). */}
        {policy && <AgentDetails source={agentDetailsSourceFromPolicy(policy)} collapsible />}
        {showHoldCards && (
          <Section title="Hold Cards" right={`${activeHoldCards().length}/${allActiveHoldCards().length} Hold Cards`}>
            <HoldCards
              state={policy.state}
              activeHoldCards={activeHoldCards()}
              allActiveHoldCards={allActiveHoldCards()}
            />
          </Section>
        )}
        <Section
          classes={{ container: classes.firstContainer }}
          title="Policy Change History"
          right={`${versions.length}/${total} Versions`}
        >
          {loading || !policy ? (
            <Loading />
          ) : (
            <Versions
              versions={versions}
              state={policy.state}
              allVersions={list}
              currentVersion={policy.version}
              policyType={policy.policyType}
            />
          )}
        </Section>
        <Section
          title="Endorsement History"
          right={sectionCount(isBranchEnv, policy ? (history || []).length : 0)}
          classes={{ header: classes.sectionHeader }}
        >
          <div onClick={() => setActiveSection(SegmentGroup.history)}>
            {!loading && (
              <SegmentList
                segments={history} // Slice to prevent original object mutation
                policyType={policy ? policy.policyType : ''}
                isSectionActive={activeSection === 'history'}
              />
            )}
          </div>
        </Section>
        {renewal.length > 0 && (
          <Section
            title={renewalSectionTitle(isBranchEnv)}
            right={sectionCount(isBranchEnv, policy ? (renewal || []).length : 0)}
            classes={{ header: classes.sectionHeader }}
          >
            <div onClick={() => setActiveSection(SegmentGroup.renewal)}>
              {!loading && (
                <SegmentList
                  segments={renewal} // Slice to prevent original object mutation
                  policyType={policy ? policy.policyType : ''}
                  isSectionActive={activeSection === 'renewal'}
                />
              )}
            </div>
          </Section>
        )}
      </Provider>
    </div>
  );
};

PolicySidebar.propTypes = {
  store: PropTypes.object.isRequired
};

export default flowRight(withMoratorium, withStore)(PolicySidebar);
